import { useMemo } from 'react';
import { useListTasksPaginated } from './useListTasksPaginated';
import { TaskStatsCalculator, type TaskStats } from '@src/application/tasks/utils/task-stats.calculator';

/**
 * Hook: useTasksWithStats
 * Fetches recent tasks and provides computed statistics
 *
 * This hook encapsulates the business logic of calculating task statistics,
 * keeping the presentation layer clean.
 *
 * @returns {Object} - Tasks, stats, and query state
 * @example
 * ```typescript
 * const { tasks, stats, isLoading, error, refetch } = useTasksWithStats();
 *
 * return (
 *   <div>
 *     <p>Total: {stats.total}</p>
 *     <p>Completed: {stats.completed}</p>
 *     <p>Completion Rate: {stats.completionRate}%</p>
 *   </div>
 * );
 * ```
 */
export function useTasksWithStats() {
    // Get first page of tasks (most recent) with a reasonable limit
    const { data, isLoading, error, refetch } = useListTasksPaginated({ limit: 50 });

    // Flatten the paginated data
    const tasks = useMemo(() => {
        return data?.pages.flatMap(page => page.tasks) || [];
    }, [data]);

    // Get total count from the first page
    const totalCount = useMemo(() => {
        return data?.pages[0]?.total || tasks.length;
    }, [data, tasks.length]);

    // Calculate stats using domain logic (memoized for performance)
    const stats: TaskStats = useMemo(() => {
        const calculated = TaskStatsCalculator.calculate(tasks);
        // Override total with the actual total from API
        return {
            ...calculated,
            total: totalCount,
        };
    }, [tasks, totalCount]);

    return {
        tasks,
        stats,
        isLoading,
        error,
        refetch,
    };
}

/**
 * Hook: useRecentTasks
 * Fetches tasks and returns only the most recent ones
 * 
 * @param limit - Number of recent tasks to return
 * @returns {Object} - Recent tasks, stats, and query state
 * @example
 * ```typescript
 * const { tasks, stats, isLoading } = useRecentTasks(5);
 * ```
 */
export function useRecentTasks(limit: number = 5) {
    const { tasks: allTasks, stats, isLoading, error, refetch } = useTasksWithStats();

    const recentTasks = useMemo(() => {
        return TaskStatsCalculator.getMostRecent(allTasks, limit);
    }, [allTasks, limit]);

    return {
        tasks: recentTasks,
        stats,
        isLoading,
        error,
        refetch,
    };
}
